import { call, all, put, select, takeLatest } from 'redux-saga/effects';
import {
  bundlesActions,
  bundleSelector,
  bundlesFormSelector,
} from '$store/bundles/index';
import { imSelector } from '$store/im';
import { RootState } from '$store/rootReducer';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import {
  attachActions,
  attachFullItemsSelector,
  attachItemsSelector,
} from '$store/attach';
import { t } from '$translation';
import { goBack, hideModal } from '$navigation/helper';
import { DeleteBundleAction, EditBundleAction } from '$store/bundles/interface';
import { batchActions } from '$store';
import { AttachItem, AttachType, BundleModel, CurrencyEnum } from '$store/models';
import { currentUserSelector, mainActions } from '$store/main';
import { profileActions } from '$store/profile';
import { statReachGoal } from '$utils';

function* updateBundlePreview(bundle: BundleModel) {
  const previewsFull = yield select((state: RootState) =>
    attachFullItemsSelector(state, `bundle_previews`),
  );

  const contentFull = yield select((state: RootState) =>
    attachFullItemsSelector(state, `bundle_content`),
  );

  const coverUrl = previewsFull.length
    ? previewsFull[0]?.type === AttachType.PHOTO
      ? previewsFull[0]?.photo?.photoLarge || ''
      : previewsFull[0]?.video?.photoLarge || ''
    : '';

  return {
    ...bundle,
    coverUrl,
    previews: previewsFull,
    content: contentFull,
  };
}

function* createBundleWorker() {
  try {
    const form = (yield select(bundlesFormSelector)) as RootState['bundles']['form'];
    yield put(toastActions.setToastLoading());
    const user = yield select(currentUserSelector);

    const previews: AttachItem[] = yield select((state: RootState) =>
      attachItemsSelector(state, `bundle_previews`),
    );

    const content = yield select((state: RootState) =>
      attachItemsSelector(state, `bundle_content`),
    );

    if ([previews, content].includes('photo_uploading')) {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    const data = yield call(Api.post, '/bundles/create', {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      priceWithoutDiscount: parseFloat(form.priceWithoutDiscount),
      previews,
      content,
    });

    const bundle = yield updateBundlePreview({
      userId: user.id,
      coverUrl: '',
      id: data.id,
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      priceWithoutDiscount: parseFloat(form.priceWithoutDiscount),
      thumbnail: '',
      currency: CurrencyEnum.EUR,
      previews,
      content,
    });

    yield put(bundlesActions.setBundle(bundle));
    yield put(
      profileActions.addBundleId({
        userId: user.id,
        bundleId: bundle.id,
      }),
    );

    yield put(toastActions.setToastSuccess('Bundle created!'));
    yield call(statReachGoal, 'bundles_create_success', {
      bundleId: data.id,
    });
    yield put(bundlesActions.clearForm());
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* editBundleWorker(action: EditBundleAction) {
  try {
    const { bundle, decryptKey } = yield call(
      Api.get,
      `/bundles/${action.payload}/info`,
      {},
    );
    yield put(
      batchActions(
        bundlesActions.setFromId(bundle.id),
        bundlesActions.setFromPrice(bundle.price),
        bundlesActions.setFromPriceWithoutDiscount(bundle.priceWithoutDiscount),
        bundlesActions.setFromName(bundle.name),
        bundlesActions.setFromDescription(bundle.description),
        bundlesActions.setDecryptKey({
          bundleId: bundle.id,
          decryptKey: decryptKey,
        }),
        attachActions.initFromAttachments({
          pickerId: 'bundle_content',
          attachments: bundle.content,
        }),
        attachActions.initFromAttachments({
          pickerId: 'bundle_previews',
          attachments: bundle.previews,
        }),
      ),
    );
  } catch (e) {}
}

function* saveBundleWorker() {
  const form = (yield select(bundlesFormSelector)) as RootState['bundles']['form'];
  const bundle = (yield select((root: RootState) =>
    bundleSelector(root, form.id),
  )) as BundleModel;
  try {
    yield put(toastActions.setToastLoading());

    const previews = yield select((state: RootState) =>
      attachItemsSelector(state, `bundle_previews`),
    );

    const content = yield select((state: RootState) =>
      attachItemsSelector(state, `bundle_content`),
    );

    if ([previews, content].includes('photo_uploading')) {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    const data = yield call(Api.post, `/bundles/${form.id}/save`, {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      priceWithoutDiscount: parseFloat(form.priceWithoutDiscount),
      previews,
      content,
    });

    yield put(
      bundlesActions.setBundle(
        yield updateBundlePreview({
          ...bundle,
          name: form.name,
          description: form.description,
          price: parseFloat(form.price),
          priceWithoutDiscount: parseFloat(form.priceWithoutDiscount),
        }),
      ),
    );

    yield put(toastActions.setToastSuccess(t('bundles_bundle_saved')));
    yield put(bundlesActions.clearForm());
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* clearFormWorker() {
  yield put(attachActions.clear('bundle_previews'));
  yield put(attachActions.clear('bundle_content'));
}

function* getBundleWorker(action: EditBundleAction) {
  try {
    yield put(bundlesActions.setFailed(null));
    const { bundle, decryptKey, user } = yield call(
      Api.get,
      `/bundles/${action.payload}/info`,
      {},
    );
    yield put(
      batchActions(
        bundlesActions.setBundle(bundle),
        bundlesActions.setDecryptKey({
          bundleId: bundle.id,
          decryptKey: decryptKey,
        }),
        mainActions.setUser(user),
      ),
    );
  } catch (e) {
    yield put(bundlesActions.setFailed(e.code));
  }
}

export function* deleteBundleWorker(action: DeleteBundleAction) {
  try {
    const user = yield select(currentUserSelector);
    const bundleId = action.payload;
    yield put(toastActions.setToastLoading());
    yield call(Api.get, `/bundles/${action.payload}/delete`);
    yield put(
      profileActions.removeBundleId({
        userId: user.id,
        bundleId,
      }),
    );
    yield put(toastActions.setToastSuccess('Bundle deleted'));
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* bundlesSaga() {
  yield all([
    takeLatest(bundlesActions.createBundle, createBundleWorker),
    takeLatest(bundlesActions.saveBundle, saveBundleWorker),
    takeLatest(bundlesActions.editBundle, editBundleWorker),
    takeLatest(bundlesActions.clearForm, clearFormWorker),
    takeLatest(bundlesActions.getBundle, getBundleWorker),
    takeLatest(bundlesActions.deleteBundle, deleteBundleWorker),
  ]);
}
